import React, { FunctionComponent, useEffect, useRef } from 'react';
import { Col, Row } from 'react-flexbox-grid';
import { ErrorMessage, ProductFormFieldChangeHandler } from '../../../../../types/interfaces';
import { getTextFactoryReturnType } from '../../../../../utils/TextUtils';
import { ProductFormCheckbox, ProductFormInput, ProductFormSelect } from '../../../../../components/productFormFields';
import { EMPLOYMENT_VALUES, filterReferenceProfessionalRelationTypes } from '../constants';
import { alphaOptionalRegex, fullNameRegex } from '../../../../../utils/regex';
import { replaceUnsupportedCharacters } from '../../../utils';

type ReferenceProfessionalFormGroupProps = {
  getText: getTextFactoryReturnType;
  getGlobalText: getTextFactoryReturnType;
  formatErrorMessage: (error: ErrorMessage) => string;
  referenceProfessionalInd?: boolean;
  referenceProfessionalRelationTypeCd?: string;
  change: ProductFormFieldChangeHandler;
};

const ReferenceProfessionalFormGroup: FunctionComponent<ReferenceProfessionalFormGroupProps> = (
  props: ReferenceProfessionalFormGroupProps,
) => {
  const {
    getText,
    getGlobalText,
    formatErrorMessage,
    referenceProfessionalInd,
    referenceProfessionalRelationTypeCd,
    change,
  } = props;

  const prevReferenceProfessionalIndRef = useRef<boolean | undefined>(referenceProfessionalInd);
  const prevRelationTypeCdRef = useRef<string | undefined>(referenceProfessionalRelationTypeCd);
  const isOtherProRelationType =
    referenceProfessionalRelationTypeCd &&
    referenceProfessionalRelationTypeCd === EMPLOYMENT_VALUES.TRADING_RELATION_STATUS_OTHER;

  // Clear dependent fields when checkbox is unchecked
  useEffect(() => {
    const prevValue = prevReferenceProfessionalIndRef.current;
    if (prevValue === true && referenceProfessionalInd === false) {
      change('proInvolvement.referenceProfessionalSecuritiesFirmName', null);
      change('proInvolvement.referenceProfessionalRelationTypeCd', null);
      change('proInvolvement.referenceProfessionalRelationTypeDetails', null);
      change('proInvolvement.referenceProfessionalFullName', null);
    }

    prevReferenceProfessionalIndRef.current = referenceProfessionalInd;
  }, [referenceProfessionalInd, change]);

  // Clear relationship details when relation type is changed from 'Other' to a different value
  useEffect(() => {
    const prevValue = prevRelationTypeCdRef.current;
    const isChangingFromOtherToNonOther =
      prevValue === EMPLOYMENT_VALUES.TRADING_RELATION_STATUS_OTHER &&
      referenceProfessionalRelationTypeCd !== EMPLOYMENT_VALUES.TRADING_RELATION_STATUS_OTHER;
    if (isChangingFromOtherToNonOther) {
      change('proInvolvement.referenceProfessionalRelationTypeDetails', null);
    }

    prevRelationTypeCdRef.current = referenceProfessionalRelationTypeCd;
  }, [referenceProfessionalRelationTypeCd, isOtherProRelationType, change]);

  const handleSecurityFirmNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    change('proInvolvement.referenceProfessionalSecuritiesFirmName', replaceUnsupportedCharacters(value));
  };

  return (
    <>
      <Col xs={12} md={4} lg={3} data-test="field_referenceProfessionalInd">
        <ProductFormCheckbox
          className="checkbox-form"
          label={getText('yourFamilyCheckbox')}
          name="proInvolvement.referenceProfessionalInd"
          formatErrorMessage={formatErrorMessage}
        />
      </Col>

      {referenceProfessionalInd && (
        <Col sm={12} md={7} lg={8}>
          <Row className="mb-6 answer">
            <Col xs={12} md={12} data-test="field_referenceProfessionalSecuritiesFirmName">
              <ProductFormInput
                handleOnChange={handleSecurityFirmNameChange}
                name="proInvolvement.referenceProfessionalSecuritiesFirmName"
                label={getText('securitiesFirmName')}
                maxLength={254}
                formatErrorMessage={formatErrorMessage}
              />
            </Col>
            <Col xs={12} md={12} data-test="field_proInvolvementRelation">
              <ProductFormSelect
                name="proInvolvement.referenceProfessionalRelationTypeCd"
                placeholder={getGlobalText('select')}
                label={getText('proInvolvementRelation')}
                formatErrorMessage={formatErrorMessage}
                filter={filterReferenceProfessionalRelationTypes}
              />
            </Col>

            {isOtherProRelationType && (
              <Col xs={12} md={12} data-test="field_referenceProfessionalRelationTypeDetails">
                <ProductFormInput
                  name="proInvolvement.referenceProfessionalRelationTypeDetails"
                  label={getText('proInvolvementRelationOther')}
                  maxLength={32}
                  regex={alphaOptionalRegex}
                  formatErrorMessage={formatErrorMessage}
                />
              </Col>
            )}

            <Col xs={12} md={12} data-test="field_referenceProfessionalFullName">
              <ProductFormInput
                name="proInvolvement.referenceProfessionalFullName"
                className="mb-3"
                label={getText('personName')}
                maxLength={32}
                regex={fullNameRegex}
                formatErrorMessage={formatErrorMessage}
              />
            </Col>
          </Row>
        </Col>
      )}
    </>
  );
};

export default ReferenceProfessionalFormGroup;
